import React, { FC, useRef } from 'react';
import { FormContext } from './FormContext';

type NAUFormProps = {
  children: React.ReactNode;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
};

const NAUForm: FC<NAUFormProps> = ({ children, onSubmit }) => {
  const inputsRef = useRef<{
    [key: string]: {
      ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
      hasError: boolean;
    };
  }>({});
  const validationFnsRef = useRef<{ [key: string]: () => boolean }>({});

  const registerInput = (
    name: string,
    ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    inputsRef.current[name] = { ref, hasError: false };
  };

  const setInputError = (name: string, hasError: boolean) => {
    if (inputsRef.current[name]) {
      inputsRef.current[name].hasError = hasError;
    }
  };

  const getInputs = () => inputsRef.current;

  const registerValidation = (name: string, validateFn: () => boolean) => {
    validationFnsRef.current[name] = validateFn;
  };

  const runAllValidations = () => {
    for (const name in validationFnsRef.current) {
      if (!validationFnsRef.current[name]()) {
        inputsRef.current[name]?.ref.current?.focus();
        return false;
      }
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!runAllValidations()) return;
    const allInputs = getInputs();
    for (const key in allInputs) {
      if (allInputs[key].hasError) {
        allInputs[key].ref.current?.focus();
        return;
      }
    }
    onSubmit(e);
  };

  return (
    <FormContext.Provider
      value={{
        registerInput,
        setInputError,
        getInputs,
        registerValidation,
        runAllValidations,
      }}
    >
      <form onSubmit={handleSubmit}>{children}</form>
    </FormContext.Provider>
  );
};

export default NAUForm;